import { useMemo } from 'react';
import { isNotDefined, isDefined, listToMap } from '@togglecorp/fujs';
import { getChartDimensions, getScaleFunction, getYDomain, getIntervals } from '../utils/chart.js';
import { formatNumber } from '../utils/common.js';
import { NUM_X_AXIS_TICKS_MIN, defaultChartMargin, defaultChartPadding, DEFAULT_Y_AXIS_WIDTH, DEFAULT_X_AXIS_HEIGHT } from '../utils/constants.js';
import useSizeTracking from './useSizeTracking.js';

function useNumericChartData(data, options) {
  const {
    keySelector,
    xValueSelector,
    yValueSelector,
    chartMargin = defaultChartMargin,
    chartPadding = defaultChartPadding,
    containerRef,
    numYAxisTicks = 6,
    xAxisTickLabelSelector,
    yAxisTickLabelSelector,
    yAxisWidth = DEFAULT_Y_AXIS_WIDTH,
    xAxisHeight = DEFAULT_X_AXIS_HEIGHT,
    yDomain,
    yValueStartsFromZero,
    yScale = 'linear',
    chartGroupingMode = 'none'
  } = options;
  const chartSize = useSizeTracking(containerRef);
  const chartData = useMemo(() => data?.map((datum, i) => {
    const key = keySelector(datum, i);
    const xValue = xValueSelector(datum, i);
    const yValue = yValueSelector(datum, i);
    if (isNotDefined(xValue) || isNotDefined(yValue)) {
      return undefined;
    }
    return {
      key,
      originalData: datum,
      xValue,
      yValue
    };
  }).filter(isDefined) ?? [], [data, keySelector, xValueSelector, yValueSelector]);
  const dataDomain = useMemo(() => {
    const min = 0;
    const max = chartData.length;
    return {
      min,
      max
    };
  }, [chartData]);
  const chartDomain = useMemo(() => {
    const diff = dataDomain.max - dataDomain.min;
    if (diff >= NUM_X_AXIS_TICKS_MIN) {
      return dataDomain;
    }
    return {
      min: 0,
      max: NUM_X_AXIS_TICKS_MIN
    };
  }, [dataDomain]);
  const numXAxisTicks = useMemo(() => {
    const diff = chartDomain.max - chartDomain.min;
    return diff;
  }, [chartDomain]);
  const {
    dataAreaSize,
    dataAreaOffset
  } = useMemo(() => getChartDimensions({
    chartSize,
    chartMargin,
    chartPadding,
    xAxisHeight,
    yAxisWidth,
    numXAxisTicks
  }), [chartSize, chartMargin, chartPadding, xAxisHeight, yAxisWidth, numXAxisTicks]);
  const xAxisDomain = useMemo(() => ({
    min: chartDomain.min,
    max: chartDomain.max - 1
  }), [chartDomain]);
  const xScaleFn = useMemo(() => getScaleFunction(xAxisDomain, {
    min: 0,
    max: chartSize.width
  }, {
    start: dataAreaOffset.left,
    end: dataAreaOffset.right
  }), [dataAreaOffset, xAxisDomain, chartSize.width]);
  const yAxisDomain = useMemo(() => {
    if (isDefined(yDomain)) {
      return yDomain;
    }
    return getYDomain(chartData, numYAxisTicks, yValueStartsFromZero, chartGroupingMode);
  }, [chartData, yDomain, yValueStartsFromZero, numYAxisTicks, chartGroupingMode]);
  const yScaleFn = useMemo(() => getScaleFunction(yAxisDomain, {
    min: 0,
    max: chartSize.height
  }, {
    start: dataAreaOffset.top,
    end: dataAreaOffset.bottom
  }, true, yScale), [yAxisDomain, dataAreaOffset, chartSize.height, yScale]);
  const chartPoints = useMemo(() => chartData.map((datum, i) => ({
    ...datum,
    x: xScaleFn(i),
    y: listToMap(datum.yValue.map(({
      key,
      value
    }) => isNotDefined(value) ? undefined : {
      key,
      value: yScaleFn(value)
    }).filter(isDefined), ({
      key
    }) => key, ({
      value
    }) => value)
  })), [chartData, xScaleFn, yScaleFn]);
  const xAxisTicks = useMemo(() => {
    const ticks = chartData.map((datum, i) => ({
      key: datum.key,
      x: xScaleFn(i),
      label: xAxisTickLabelSelector ? xAxisTickLabelSelector(datum.originalData, i) : datum.xValue
    }));
    if (ticks.length >= NUM_X_AXIS_TICKS_MIN) {
      return ticks;
    }
    const diff = NUM_X_AXIS_TICKS_MIN - ticks.length;
    const additionalTicks = Array.from(Array(diff).keys()).map((_, i) => ({
      key: `additional-tick-${i}`,
      x: xScaleFn(ticks.length + i),
      label: ''
    }));
    return [...ticks, ...additionalTicks];
  }, [chartData, xScaleFn, xAxisTickLabelSelector]);
  const yAxisTicks = useMemo(() => getIntervals(yAxisDomain, numYAxisTicks).map((tick, i) => ({
    y: yScaleFn(tick),
    label: yAxisTickLabelSelector ? yAxisTickLabelSelector(tick, i) : formatNumber(tick, {
      compact: true
    }) ?? ''
  })), [yAxisDomain, yScaleFn, numYAxisTicks, yAxisTickLabelSelector]);
  return useMemo(() => ({
    chartPoints,
    xAxisTicks,
    yAxisTicks,
    chartSize,
    xScaleFn,
    yScaleFn,
    dataAreaSize,
    dataAreaOffset,
    xAxisHeight,
    yAxisWidth,
    chartMargin,
    numXAxisTicks
  }), [chartPoints, xAxisTicks, yAxisTicks, chartSize, xScaleFn, yScaleFn, dataAreaSize, dataAreaOffset, chartMargin, numXAxisTicks, xAxisHeight, yAxisWidth]);
}

export { useNumericChartData as default };
//# sourceMappingURL=useCategoricalChartData.js.map
